import Button from 'components/base/Button';
import { Col, Form, Row, Modal } from 'react-bootstrap';
import ModalForm from 'components/forms/ModalForm';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  CREATE_LOCATION,
  PATCH_LOCATION,
  LocationDTO
} from 'services/api/location';
import { FormProps } from 'data/from-data/interface/modal-form-props.interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';

export class LocationFormData {
  [key: string]: FormProps;

  location: FormProps = {
    name: 'location',
    label: 'Location',
    type: 'text',
    placeholder: 'Location',
    required: true
  };

  name: FormProps = {
    name: 'name',
    label: 'Lat/Long',
    type: 'text',
    placeholder: '9.00, 4.5533',
    required: false
  };

  country: FormProps = {
    name: 'country',
    label: 'Country',
    type: 'text',
    placeholder: 'Country',
    required: false
  };
}

interface LocationModalProps {
  show: boolean;
  handleClose: () => void;
  isEditing?: boolean;
  location?: any;
  onSuccess?: () => void;
}

const LocationModal = ({
  show,
  handleClose,
  isEditing = false,
  location,
  onSuccess
}: LocationModalProps) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createLocationDto, setCreateLocationDto] = useState<LocationFormData>(
    new LocationFormData()
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const queries = useQueryClient();

  const createLocationAction = useMutation('createLocation', CREATE_LOCATION, {
    onError: (err: any) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'Failed to create location');
    },
    onSuccess: () => {
      setLoading(false);
      queries.invalidateQueries('get_all_location');
      setCreateLocationDto(new LocationFormData());
      handleClose();
      onSuccess?.();
      setValidated(false);
      toast.success('Location created successfully');
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  const updateLocationAction = useMutation('updateLocation', PATCH_LOCATION, {
    onError: (err: any) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'Failed to update location');
    },
    onSuccess: () => {
      setLoading(false);
      queries.invalidateQueries('get_all_location');
      handleClose();
      onSuccess?.();
      toast.success('Location updated successfully');
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  useEffect(() => {
    if (location !== undefined) {
      setCreateLocationDto(prevData => {
        const updatedData = Object.keys(prevData).reduce((acc: any, key) => {
          acc[key] = {
            ...prevData[key],
            value: location[key] || prevData[key].value
          };
          return acc;
        }, {});
        return updatedData;
      });
    }
  }, [location]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!createLocationDto.location.value) {
      event.stopPropagation();
      setValidated(true);
    } else {
      return runFormSubmissionAction(event);
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setCreateLocationDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      name: { value: name },
      location: { value: locationValue },
      country: { value: country }
    } = createLocationDto;
    const payload: LocationDTO = {
      ...createLocationDto,
      name,
      location: locationValue,
      country
    };

    if (isEditing && location) {
      const locationId = (location as any).id || location._id;
      await updateLocationAction.mutateAsync({ locationId, data: payload });
    } else {
      await createLocationAction.mutateAsync(payload);
    }
  };

  const renderFormFields = () => {
    return Object.entries(createLocationDto).map(([key, formProps]) => (
      <Col md="12" key={key}>
        <ModalForm
          formProps={formProps as unknown as FormProps}
          handleInputChange={handleInputChange}
        />
      </Col>
    ));
  };
  return (
    <Modal show={show} onHide={handleClose} className="p-0" centered>
      <Modal.Header className="border-200 p-4">
        <h5 className="modal-title text-1000 fs-6 lh-sm">
          {isEditing ? 'Edit Location' : 'Create New Location'}
        </h5>
        <Button className="p-1" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} className="fs-9" />
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-4 pb-2 px-4 mb-5">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row className="g-3 mb-5">{renderFormFields()}</Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
        <Button
          variant="phoenix-primary"
          size="sm"
          className="px-4 fs-10 my-0"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="px-9 fs-9 my-0"
          disabled={isLoading}
          type="submit"
          onClick={handleSubmit}
        >
          {isEditing ? 'Save Changes' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LocationModal;
